import { defaultI18n } from '@kilat/config-seed';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { usePlatformBrand } from './use-platform-brand.js';
import { DEFAULT_LOCALE } from '@kilat/shared';
import type { I18nString, Locale, PlatformBrand } from '@kilat/shared';

/**
 * Sprint 88 — agent-pwa i18n provider. Mirror of customer-pwa &
 * marketing providers. Resolution order:
 *   1. localStorage `kilat.locale` (sticky pick, shared across Kilat
 *      surfaces on the same origin family)
 *   2. navigator.languages[0..] (device hint)
 *   3. brand.defaultLocale (fallback)
 */
const LS_KEY = 'kilat.locale';
const LS_DETECTED_FLAG = 'kilat.localeDetected';

export interface I18n {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  readonly firstLaunchDetected: boolean;
  readonly supportedLocales: readonly Locale[];
  t(key: string, vars?: Record<string, string | number>): string;
  platformBrandCopy(key: keyof PlatformBrand['copy']): string;
}

function normaliseDeviceLocale(
  raw: string | undefined,
  supported: readonly Locale[],
): Locale | null {
  if (!raw) return null;
  const head2 = raw.toLowerCase().slice(0, 2);
  const candidate: Locale | null =
    head2 === 'en'
      ? 'en'
      : head2 === 'ms' || head2 === 'id'
        ? 'ms'
        : head2 === 'zh'
          ? 'zh'
          : head2 === 'ta'
            ? 'ta'
            : null;
  if (candidate && supported.includes(candidate)) return candidate;
  return null;
}

function detectInitialLocale(brand: PlatformBrand): { locale: Locale; isFirstLaunch: boolean } {
  try {
    const stored = window.localStorage.getItem(LS_KEY);
    if (stored && brand.supportedLocales.includes(stored as Locale)) {
      return { locale: stored as Locale, isFirstLaunch: false };
    }
  } catch {
    /* swallow */
  }
  const candidates: readonly string[] =
    typeof navigator !== 'undefined'
      ? Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : []
      : [];
  for (const tag of candidates) {
    const match = normaliseDeviceLocale(tag, brand.supportedLocales);
    if (match) {
      let isFirstLaunch = true;
      try {
        if (window.localStorage.getItem(LS_DETECTED_FLAG)) isFirstLaunch = false;
      } catch {
        /* swallow */
      }
      return { locale: match, isFirstLaunch };
    }
  }
  return { locale: brand.defaultLocale, isFirstLaunch: false };
}

function resolveDotted(obj: unknown, key: string): string | undefined {
  return key
    .split('.')
    .reduce<unknown>(
      (acc, part) =>
        acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)
          ? (acc as Record<string, unknown>)[part]
          : undefined,
      obj,
    ) as string | undefined;
}

function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_m, k: string) =>
    k in vars ? String(vars[k]) : `{{${k}}}`,
  );
}

function pickI18n(field: I18nString, locale: Locale): string {
  return field[locale] ?? field.en;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const brand = usePlatformBrand();
  const initial = useMemo(() => detectInitialLocale(brand), [brand]);
  const [locale, setLocaleState] = useState<Locale>(initial.locale);
  const [firstLaunchDetected, setFirstLaunchDetected] = useState(initial.isFirstLaunch);

  useEffect(() => {
    if (!initial.isFirstLaunch) return;
    try {
      window.localStorage.setItem(LS_DETECTED_FLAG, '1');
    } catch {
      /* swallow */
    }
  }, [initial.isFirstLaunch]);

  const setLocale = useCallback(
    (next: Locale) => {
      if (!brand.supportedLocales.includes(next)) return;
      setLocaleState(next);
      setFirstLaunchDetected(false);
      try {
        window.localStorage.setItem(LS_KEY, next);
      } catch {
        /* swallow */
      }
    },
    [brand.supportedLocales],
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo<I18n>(() => {
    const catalog = defaultI18n[locale];
    const fallback = defaultI18n['en'];
    return {
      locale,
      setLocale,
      firstLaunchDetected,
      supportedLocales: brand.supportedLocales,
      t(key, vars) {
        const found = resolveDotted(catalog, key) ?? resolveDotted(fallback, key) ?? key;
        return interpolate(found, vars);
      },
      platformBrandCopy(key) {
        return pickI18n(brand.copy[key], locale);
      },
    };
  }, [brand, locale, setLocale, firstLaunchDetected]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (v) return v;
  return {
    locale: DEFAULT_LOCALE,
    setLocale: () => undefined,
    firstLaunchDetected: false,
    supportedLocales: ['en'],
    t: (key) => key,
    platformBrandCopy: () => '',
  };
}

export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  en: 'English',
  ms: 'Bahasa Malaysia',
  zh: '中文',
  ta: 'தமிழ்',
};
